import { ThemeToggle } from '@/components/ui/ThemeToggle';
import { UserMenu } from '@/components/ui/UserMenu';
import { AuthProvider } from '@/lib/auth';
import { OrgProvider } from '@/features/lc/OrgProvider';
import { LLMContextApp, navigateTo, withBasePath } from '@/features/lc';
import { fetchMe } from '@/features/lc/api';
import { useAppPathname } from '@/features/lc/router';
import { useAuth } from '@/lib/auth';
import { DefaultPasswordBanner } from '@/features/lc/components/DefaultPasswordBanner';
import { BasicLoginGate } from '@/features/lc/components/BasicLoginGate';
import { useEffect, useRef, useState } from 'react';

function NavLink({ label, path, current }: { label: string; path: string; current: boolean }) {
  return (
    <button
      type="button"
      onClick={() => navigateTo(path)}
      className={`relative px-1 pb-0.5 text-sm font-semibold transition-colors ${
        current
          ? 'text-ink after:absolute after:inset-x-0 after:-bottom-[17px] after:h-0.5 after:rounded-full after:bg-accent'
          : 'text-dim hover:text-ink'
      }`}
    >
      {label}
    </button>
  );
}

function MobileNav({
  pathname,
  user,
  isAdmin,
}: {
  pathname: string;
  user: unknown;
  isAdmin: boolean;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onClickOutside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const go = (path: string) => {
    setOpen(false);
    navigateTo(path);
  };

  const items: { label: string; path: string; show: boolean }[] = [
    { label: 'All Libraries', path: '/search', show: true },
    { label: 'Get Started', path: '/docs', show: true },
    { label: 'Add a Library', path: '/request', show: true },
    { label: 'Dashboard', path: '/dashboard', show: !!user },
    { label: 'Admin', path: '/admin', show: isAdmin },
  ];

  return (
    <div ref={ref} className="relative sm:hidden">
      <button
        type="button"
        aria-label="Open menu"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-9 w-9 items-center justify-center text-dim transition-colors hover:bg-accent-soft hover:text-ink"
      >
        <svg
          aria-hidden="true"
          className="h-5 w-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          strokeWidth={2}
        >
          {open ? (
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
          ) : (
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
          )}
        </svg>
      </button>
      {open && (
        <div
          data-testid="mobile-nav-menu"
          className="absolute right-0 top-full z-50 mt-2 w-56 overflow-hidden border border-rule bg-panel py-1 shadow-lg"
        >
          {items
            .filter((i) => i.show)
            .map((item) => {
              const isCurrent = pathname === item.path;
              return (
                <button
                  key={item.path}
                  type="button"
                  onClick={() => go(item.path)}
                  className={`flex w-full items-center px-4 py-2 text-left text-sm transition-colors ${
                    isCurrent ? 'bg-accent-soft text-accent' : 'text-ink hover:bg-bg'
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
        </div>
      )}
    </div>
  );
}

// Rendered inside AuthProvider so useAuth/useAppPathname are safe to call here.
function AppShell() {
  const pathname = useAppPathname();
  const { user, getToken, deploymentMode } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const isLandingPage = pathname === '/' && deploymentMode === 'saas';

  useEffect(() => {
    let cancelled = false;
    void Promise.resolve(user)
      .then((nextUser) => (nextUser ? getToken() : null))
      .then((token) => (token ? fetchMe(token) : null))
      .then((me) => {
        if (!cancelled) setIsAdmin(me?.isAdmin ?? false);
      })
      .catch(() => {
        if (!cancelled) setIsAdmin(false);
      });

    return () => {
      cancelled = true;
    };
  }, [user, getToken]);

  return (
    <OrgProvider>
      <div className={isLandingPage ? 'min-h-screen bg-[#f5f5f2]' : 'min-h-screen'}>
        <DefaultPasswordBanner />
        {!isLandingPage && (
          <header className="topbar sticky top-0 z-50">
            <div className="mx-auto flex h-14 max-w-6xl items-center justify-between px-4 sm:px-6">
              <div className="flex min-w-0 items-center gap-2 sm:gap-3">
                <a
                  href={withBasePath('/')}
                  onClick={(e) => {
                    e.preventDefault();
                    navigateTo('/');
                  }}
                  className="flex min-w-0 items-center gap-2 text-sm font-bold tracking-tight text-ink"
                >
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center border border-ink text-xs font-bold lowercase text-ink">
                    lc
                  </span>
                  <span className="truncate">
                    llmcontext<span className="text-dim">.dev</span>
                  </span>
                </a>
                <span className="hidden border border-rule px-2 py-[2px] text-2xs font-medium uppercase tracking-wider text-dim sm:inline">
                  beta
                </span>
              </div>
              <div className="flex items-center gap-3 sm:gap-5">
                <nav className="hidden items-center gap-5 sm:flex">
                  <NavLink label="all libraries" path="/search" current={pathname === '/search'} />
                  <NavLink
                    label="get started"
                    path="/docs"
                    current={pathname === '/docs' || pathname === '/skills'}
                  />
                  <NavLink label="add library" path="/request" current={pathname === '/request'} />
                  {user && (
                    <NavLink
                      label="dashboard"
                      path="/dashboard"
                      current={pathname === '/dashboard'}
                    />
                  )}
                  {isAdmin && (
                    <NavLink label="admin" path="/admin" current={pathname === '/admin'} />
                  )}
                </nav>
                <ThemeToggle
                  showLabel={false}
                  className="h-9 w-9 px-0 text-dim hover:bg-accent-soft hover:text-ink"
                />
                <UserMenu guestDisplay="button" />
                <MobileNav pathname={pathname} user={user} isAdmin={isAdmin} />
              </div>
            </div>
          </header>
        )}

        {/* Page-width convention: the shell caps all pages at max-w-6xl (app /
            data / table views use the full width). Long-form reading pages
            (landing, /why, /argument) constrain themselves to max-w-3xl for a comfortable
            measure. Prefer these two widths over new arbitrary max-w values. */}
        <main className={isLandingPage ? '' : 'mx-auto max-w-6xl px-4 py-6 sm:px-6'}>
          <LLMContextApp />
        </main>
      </div>
    </OrgProvider>
  );
}

function App() {
  return (
    <AuthProvider>
      <BasicLoginGate>
        <AppShell />
      </BasicLoginGate>
    </AuthProvider>
  );
}

export default App;
